/**
 * A plain, predictable poll of one job (spec 2026-09-05-07 §1).
 *
 * Digitization and auto-calibration both take about half a minute and both used
 * to watch their job through `useDynamicRefresh`. That hook is **pinned to
 * `LIVE_INTERVAL_MS` = 5 minutes** whenever the asset scope's live socket is
 * connected, so unless a scope event happened to fire for the job, a finished
 * run sat behind a progress strip for up to five minutes.
 *
 * So: a fixed interval, not adaptive and never pinned. A scope event is welcome
 * to trigger an *extra* refetch — `bump()` is that door — but it can never
 * replace the poll. The first check lands at `firstCheckMs` so a fixture-speed
 * run (CI, e2e) settles promptly instead of waiting out the first full tick.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import { $api } from '../../api'

const JOB_PATH = '/api/v1/organizations/{orgCode}/jobs/{jobUid}' as const

/** The cadence §1 asks for. */
export const JOB_POLL_INTERVAL_MS = 10_000

/** The first check, early enough that a fast run does not wait out a tick. */
export const JOB_POLL_FIRST_CHECK_MS = 2_000

export interface PolledExecution {
    output?: unknown
    error?: unknown
    status?: string
}

export interface PolledJob {
    uid: string
    final_status?: string | null
    executions?: PolledExecution[]
}

export interface JobPoll {
    job: PolledJob | undefined
    /** `null` until the job reaches a final status. */
    finalStatus: string | null
    /** The last execution — where the output and the error live. */
    execution: PolledExecution | undefined
    /** Force an immediate re-check; a live event calls this. */
    bump: () => void
}

/** Turns a job execution's `error` (an arbitrary JSON value) into one line of text. */
export function jobErrorMessage(raw: unknown): string {
    if (!raw) return 'unknown error'
    if (typeof raw === 'string') return raw
    const obj = raw as { message?: string; error?: string }
    return obj.message ?? obj.error ?? JSON.stringify(raw)
}

export function useJobPoll(
    org: string,
    jobUid: string | null,
    opts: { intervalMs?: number; firstCheckMs?: number } = {},
): JobPoll {
    const intervalMs = opts.intervalMs ?? JOB_POLL_INTERVAL_MS
    const firstCheckMs = opts.firstCheckMs ?? JOB_POLL_FIRST_CHECK_MS
    const enabled = !!jobUid

    const query = $api.useQuery(
        'get',
        JOB_PATH,
        { params: { path: { orgCode: org, jobUid: jobUid ?? '' } } } as never,
        {
            enabled,
            // The whole point of this hook: a fixed cadence that no live socket
            // can stretch. `false` once the job has settled, so a run that
            // finished stops costing a request every ten seconds.
            refetchInterval: (q: { state: { data?: unknown } }) =>
                (q.state.data as PolledJob | undefined)?.final_status ? false : intervalMs,
            // A tab in the background is exactly where a half-minute job is
            // most likely to finish unnoticed.
            refetchIntervalInBackground: true,
            staleTime: 0,
        } as never,
    )

    const refetch = query.refetch
    const bump = useCallback(() => {
        if (enabled) void refetch()
    }, [enabled, refetch])

    // The early first check. One timer per job, cleared when the job changes,
    // so a second run never inherits the first one's pending tick.
    const firstCheckRef = useRef<string | null>(null)
    useEffect(() => {
        if (!jobUid || firstCheckRef.current === jobUid) return
        firstCheckRef.current = jobUid
        const id = window.setTimeout(() => { void refetch() }, firstCheckMs)
        return () => window.clearTimeout(id)
    }, [jobUid, firstCheckMs, refetch])

    const job = query.data as PolledJob | undefined
    const executions = job?.executions ?? []
    return {
        job,
        finalStatus: jobUid ? job?.final_status ?? null : null,
        execution: executions[executions.length - 1],
        bump,
    }
}

/**
 * The shared half of a "click a button, watch a job, report what came out"
 * flow — the part digitization and auto-calibration have in common.
 */
export interface JobRunState {
    running: boolean
    jobUid: string | null
    elapsedSeconds: number
}

/** Elapsed seconds since `startedAt`, ticking while a run is in flight. */
export function useElapsedSeconds(startedAt: number | null): number {
    const [elapsed, setElapsed] = useState(0)
    useEffect(() => {
        if (!startedAt) {
            setElapsed(0)
            return
        }
        setElapsed(Math.round((Date.now() - startedAt) / 1000))
        const id = window.setInterval(
            () => setElapsed(Math.round((Date.now() - startedAt) / 1000)),
            1000,
        )
        return () => window.clearInterval(id)
    }, [startedAt])
    return elapsed
}
